import WriteButton from "@/components/WriteButton";
import { useEffect, useRef, useState } from "react";
import { api, apiError } from "@/lib/pos-api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { DatabaseBackup, Download, Upload, RotateCcw, Trash2, Loader2, ShieldCheck, Clock } from "lucide-react";

const fmtSize = (b) => (b > 1048576 ? (b / 1048576).toFixed(1) + " MB" : b > 1024 ? (b / 1024).toFixed(1) + " KB" : b + " B");
const fmtDate = (v) => (v ? new Date(v).toLocaleString("id-ID") : "-");

export default function Backup() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState(null); // {id} | {file}
  const [confirmText, setConfirmText] = useState("");
  const [restoring, setRestoring] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try { const { data } = await api.get("/backups"); setItems(data); }
    catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const backupNow = async () => {
    setBusy(true);
    try { await api.post("/backups"); toast.success("Backup manual dibuat"); load(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const backupAndDownload = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/backups");
      const res = await api.get(`/backups/${data.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `poskasir_backup_full_${data.id}.json`; a.click(); URL.revokeObjectURL(url);
      toast.success(`Backup penuh (${data.total_docs} data) dibuat & diunduh`);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const download = async (b) => {
    try {
      const res = await api.get(`/backups/${b.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `poskasir_backup_${b.id}.json`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  const del = async (b) => {
    if (!window.confirm("Hapus backup ini?")) return;
    try { await api.delete(`/backups/${b.id}`); toast.success("Backup dihapus"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (f) { setRestoreTarget({ file: f }); setConfirmText(""); }
    e.target.value = "";
  };

  const doRestore = async () => {
    if (confirmText.trim().toUpperCase() !== "RESTORE") { toast.error('Ketik RESTORE untuk konfirmasi'); return; }
    setRestoring(true);
    try {
      if (restoreTarget.file) {
        const fd = new FormData();
        fd.append("file", restoreTarget.file);
        fd.append("confirm", confirmText.trim());
        await api.post("/backups/restore-file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      } else {
        await api.post(`/backups/${restoreTarget.id}/restore`, { confirm: confirmText.trim() });
      }
      toast.success("Restore berhasil. Memuat ulang…");
      setRestoreTarget(null);
      setTimeout(() => window.location.reload(), 1200);
    } catch (e) { toast.error(apiError(e)); }
    finally { setRestoring(false); }
  };

  return (
    <>
      <PageHeader title="Backup & Restore" subtitle="Cadangkan & pulihkan seluruh data toko">
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" accept="application/json,.json" onChange={onPickFile} className="hidden" data-testid="restore-file-input" />
          <WriteButton data-testid="restore-file-btn" variant="outline" onClick={() => fileRef.current?.click()} className="border-slate-300 bg-[#F7F9FB] hover:bg-[#F7F9FB] gap-2">
            <Upload className="h-4 w-4" /> Restore dari File
          </WriteButton>
          <WriteButton data-testid="backup-now-btn" variant="outline" onClick={backupNow} disabled={busy} className="border-slate-300 bg-[#F7F9FB] hover:bg-slate-100 gap-2">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <DatabaseBackup className="h-4 w-4" />} Backup Sekarang
          </WriteButton>
          <WriteButton data-testid="backup-download-full-btn" onClick={backupAndDownload} disabled={busy} className="bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-bold gap-2">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Backup & Unduh (Full)
          </WriteButton>
        </div>
      </PageHeader>

      <div className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="backup-page">
        <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 flex items-center gap-3" data-testid="auto-backup-status">
          <ShieldCheck className="h-5 w-5 text-emerald-600 shrink-0" />
          <div className="text-sm">
            <div className="font-semibold text-emerald-600">Backup otomatis aktif</div>
            <div className="text-emerald-600/70 text-xs">Snapshot otomatis mengikuti jadwal backup yang diatur admin master (disimpan {20} terakhir). Backup manual tersedia dari akun tenant.</div>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-slate-50 overflow-x-auto">
          <table className="w-full text-sm" data-testid="backup-table">
            <thead className="text-slate-500 text-[11px] uppercase tracking-wide border-b border-slate-200">
              <tr>
                <th className="text-left py-2 px-3">Waktu</th>
                <th className="text-left py-2 px-3">Jenis</th>
                <th className="text-left py-2 px-3">Pemicu</th>
                <th className="text-right py-2 px-3">Total Data</th>
                <th className="text-right py-2 px-3">Ukuran</th>
                <th className="text-center py-2 px-3">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan={6} className="py-10 text-center text-slate-500"><Clock className="h-6 w-6 mx-auto mb-2 opacity-50" />Belum ada backup</td></tr>
              ) : items.map((b) => (
                <tr key={b.id} data-testid={`backup-row-${b.id}`} className="border-b border-slate-200 hover:bg-slate-50">
                  <td className="py-2 px-3 text-slate-600">{fmtDate(b.created_at)}</td>
                  <td className="py-2 px-3">
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${b.kind === "manual" ? "bg-blue-500/15 text-blue-300" : "bg-slate-200/70 text-slate-600"}`}>
                      {b.kind === "manual" ? "Manual" : "Otomatis"}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-slate-500">{b.trigger || "-"}</td>
                  <td className="py-2 px-3 text-right font-mono">{b.total_docs}</td>
                  <td className="py-2 px-3 text-right font-mono text-slate-500">{fmtSize(b.size)}</td>
                  <td className="py-2 px-3">
                    <div className="flex items-center justify-center gap-1">
                      <button data-testid={`backup-download-${b.id}`} onClick={() => download(b)} title="Unduh" className="h-8 w-8 flex items-center justify-center rounded-lg border border-slate-300 bg-[#F7F9FB] text-slate-600 hover:text-emerald-600 hover:border-emerald-500/50">
                        <Download className="h-4 w-4" />
                      </button>
                      <WriteButton plain data-testid={`backup-restore-${b.id}`} onClick={() => { setRestoreTarget({ id: b.id }); setConfirmText(""); }} title="Restore" className="h-8 w-8 flex items-center justify-center rounded-lg border border-slate-300 bg-[#F7F9FB] text-amber-600 hover:bg-amber-500/15 hover:border-amber-500/50">
                        <RotateCcw className="h-4 w-4" />
                      </WriteButton>
                      <WriteButton plain data-testid={`backup-delete-${b.id}`} onClick={() => del(b)} title="Hapus" className="h-8 w-8 flex items-center justify-center rounded-lg border border-slate-300 bg-[#F7F9FB] text-slate-500 hover:text-red-600 hover:border-red-500/50">
                        <Trash2 className="h-4 w-4" />
                      </WriteButton>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!restoreTarget} onOpenChange={(o) => { if (!o) setRestoreTarget(null); }}>
        <DialogContent data-testid="restore-dialog" className="bg-[#F7F9FB] border-slate-300 text-slate-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-500">Konfirmasi Restore</DialogTitle>
            <DialogDescription className="text-slate-500">
              Restore akan <b className="text-red-500">menimpa & mengganti SELURUH data saat ini</b> dengan isi backup
              {restoreTarget?.file ? ` dari file "${restoreTarget.file.name}"` : " terpilih"}. Tindakan ini tidak bisa dibatalkan.
              Ketik <b className="text-slate-800">RESTORE</b> untuk melanjutkan.
            </DialogDescription>
          </DialogHeader>
          <Input
            data-testid="restore-confirm-input"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="Ketik RESTORE"
            className="bg-[#F7F9FB] border-slate-300 font-mono tracking-widest text-center"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRestoreTarget(null)} className="border-slate-300 bg-[#F7F9FB] hover:bg-slate-200">Batal</Button>
            <WriteButton
              data-testid="restore-confirm-btn"
              onClick={doRestore}
              disabled={restoring || confirmText.trim().toUpperCase() !== "RESTORE"}
              className="bg-red-600 hover:bg-red-700 text-white gap-2"
            >
              {restoring ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />} Timpa & Restore
            </WriteButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
